/**
 * personalization-schema.js
 * Viori Personalizer — State Schema & Migrations
 *
 * Defines STATE_SCHEMA (single source of truth for all state keys) and
 * MIGRATIONS (jump-table for schema version upgrades).
 *
 * Must load before personalization-state.js.
 * Spec refs: Section 3, 3a, 3b
 */

(function (root) {
  'use strict';

  // Current schema version. Bump this whenever STATE_SCHEMA changes and ship
  // a corresponding migration in MIGRATIONS below.
  var CURRENT_VERSION = 1;

  // ── STATE_SCHEMA ───────────────────────────────────────────────────────────
  // Every valid personalization state key.
  // Fields: type, values (enum, optional), default, category, alias, description.
  // Metafield path: viori_personalization.<category>_<alias>
  // Spec Section 3b: flat key-value, no nesting deeper than one level.
  var STATE_SCHEMA = {
    // ── Quiz answers — Tier 1 (primary personalization driver) ───────────────
    quiz_whoIsThisFor: {
      type: 'string', default: null, category: 'quiz', alias: 'whoIsThisFor',
      values: ['myself', 'mens', 'kids', 'pet', 'gift'],
      description: 'Who the visitor is shopping for',
    },
    quiz_hairGoals: {
      type: 'string[]', default: [], category: 'quiz', alias: 'hairGoals',
      values: ['hairGrowth','scalpHydration','repairDryDamaged',
               'improveShineSoftness','balanceScalpOil','sensitiveScalpCare'],
      description: 'Selected hair goals (multi-select)',
    },
    quiz_hairType: {
      type: 'string', default: null, category: 'quiz', alias: 'hairType',
      values: ['oily','normal','dry','dryScalpCondition'],
      description: 'Hair type (single-select)',
    },
    quiz_hairConcerns: {
      type: 'string[]', default: [], category: 'quiz', alias: 'hairConcerns',
      values: ['thinning','dandruff','sensitiveScalp','colorTreated',
               'flatHair','damagedFrizzy','noneHealthyHair'],
      description: 'Hair concerns (multi-select)',
    },
    quiz_hairLookALike: {
      type: 'string', default: null, category: 'quiz', alias: 'hairLookALike',
      values: ['blonde','naturallyBlonde','brunette','blackHair',
               'redHair','coloredHair','brownCurly','blackCurly'],
      description: 'Hair look-a-like (single-select)',
    },
    quiz_otherReasons: {
      type: 'string[]', default: [], category: 'quiz', alias: 'otherReasons',
      values: ['clean','sustainable','missionBased','professional'],
      description: 'Other purchase motivations (multi-select)',
    },
    quiz_quizComplete: {
      type: 'boolean', default: false, category: 'quiz', alias: 'quizComplete',
      description: 'Whether the visitor completed the full quiz',
    },

    // ── Browsing behavior — Tier 2 ───────────────────────────────────────────
    browse_productsViewed: {
      type: 'string[]', default: [], category: 'browse', alias: 'productsViewed',
      description: 'Product handles viewed on homepage (FIFO cap 50)',
    },

    // ── UTM attribution — Tier 3 ─────────────────────────────────────────────
    utm_source:   { type: 'string', default: null, category: 'utm', alias: 'utmSource',   description: 'UTM source from landing URL' },
    utm_medium:   { type: 'string', default: null, category: 'utm', alias: 'utmMedium',   description: 'UTM medium from landing URL' },
    utm_campaign: { type: 'string', default: null, category: 'utm', alias: 'utmCampaign', description: 'UTM campaign from landing URL' },

    // ── System metadata ──────────────────────────────────────────────────────
    meta_stateVersion:   { type: 'number',  default: CURRENT_VERSION, category: 'meta', description: 'Schema version for migration logic' },
    meta_createdAt:      { type: 'number',  default: null,            category: 'meta', description: 'Unix timestamp (ms) of first state creation' },
    meta_updatedAt:      { type: 'number',  default: null,            category: 'meta', description: 'Unix timestamp (ms) of most recent write' },
    meta_lsUnavailable:  { type: 'boolean', default: false,           category: 'meta', description: 'True when falling back to in-memory storage' },
  };

  // ── MIGRATIONS (jump-table) ────────────────────────────────────────────────
  // Maps stored stateVersion → function that migrates directly to CURRENT_VERSION.
  // Adding a new schema version: bump CURRENT_VERSION, add migration here.
  // For 3+ versions behind, write a compound function (spec Section 3a).
  var MIGRATIONS = {
    // v0 → v1: map legacy unnamespaced keys to namespaced storage format
    0: function (stored) {
      var fresh = _buildDefault();
      var legacy = {
        whoIsThisFor: 'quiz_whoIsThisFor', hairGoals: 'quiz_hairGoals',
        hairType: 'quiz_hairType',         hairConcerns: 'quiz_hairConcerns',
        hairLookALike: 'quiz_hairLookALike', otherReasons: 'quiz_otherReasons',
        productsViewed: 'browse_productsViewed',
        utmSource: 'utm_source', utmMedium: 'utm_medium', utmCampaign: 'utm_campaign',
        quizComplete: 'quiz_quizComplete',
      };
      for (var old in legacy) {
        if (stored[old] !== undefined) fresh[legacy[old]] = stored[old];
      }
      fresh.meta_stateVersion = 1;
      return fresh;
    },
    // Future migrations go here:
    // 1: function(stored) { /* v1 → v2 */ }
  };

  // ── Internal helper used by migration v0 ─────────────────────────────────
  function _buildDefault() {
    var s = {};
    for (var k in STATE_SCHEMA) {
      var def = STATE_SCHEMA[k].default;
      s[k] = Array.isArray(def) ? [] : def;
    }
    s.meta_stateVersion = CURRENT_VERSION;
    s.meta_createdAt    = Date.now();
    s.meta_updatedAt    = Date.now();
    return s;
  }

  // ── Exports ────────────────────────────────────────────────────────────────
  root.VIORI_STATE_SCHEMA    = STATE_SCHEMA;
  root.VIORI_MIGRATIONS      = MIGRATIONS;
  root.VIORI_SCHEMA_VERSION  = CURRENT_VERSION;

}(window));
